// Human in the loop — "Patient credit" items (D198, H8_HITL_PATIENT_CREDIT).
// A payer paid the practice after the patient had already paid in full, so the
// patient's account holds a credit. ELVA reports and reminds; a person decides.
// ELVA never refunds, moves or applies it.
import type { GroupKey, HitlItem, PatientCredit, Try } from './types';
import { addDays, daysBetween, fmtMonthDay } from './dates';
import { PMS } from '@/features/shared/pms';

/** California: tell the patient within 90 days of posting; the item appears at day 30. */
export const CA_NOTICE_DAYS = 90;
export const CA_APPEAR_DAYS = 30;
/** Any other state: the item appears at day 60; no deadline. */
export const OTHER_APPEAR_DAYS = 60;
/** "Remind me" is not offered once this few days remain before a California deadline. */
export const REMIND_CUTOFF_DAYS = 7;

/** The date the California scenario sets as today (Nov 16, 2026). */
export const CALIFORNIA_TODAY_ISO = '2026-11-16';

/**
 * Real /elva-work/log entries for credit events. None exist yet in ELVA's work,
 * so the lines show as plain text. Fill these in when the log gets them.
 */
export const CREDIT_LOG_IDS: { posted: string; checked: string } = { posted: '', checked: '' };

/** One short note per state researched so far. Others use the fallback. */
const STATE_NOTES: Record<string, string> = {
  Texas: 'No refund deadline found for Texas. A credit left for 3 years must be reported to the state as unclaimed property.',
};

export function stateNote(stateName: string): string {
  return STATE_NOTES[stateName] ?? `No refund deadline found for ${stateName}. Ask whether a credit left on an account has to be reported to the state as unclaimed property.`;
}

export const CA_RULE_TITLE = 'Business and Professions Code §732';
export const CA_RULE_TEXT =
  'When a payer pays the practice for something the patient already paid for, the practice has to tell the patient within 90 days of when it knew (or should have known). It then refunds within 30 days, unless the patient asks to keep the money as credit. If the patient asks for a refund, it’s due within 30 days of asking. ELVA posting the payment is when the practice knows, so the 90 days start on the posting date. This is a summary, not legal advice.';

/** Today / This week / Later by days left. No deadline goes to Later. */
export function creditGroup(daysLeft: number | undefined): GroupKey {
  if (daysLeft === undefined) return 'later';
  if (daysLeft <= 0) return 'now';
  if (daysLeft <= REMIND_CUTOFF_DAYS) return 'week';
  return 'later';
}

interface Seed {
  id: string;
  patientName: string;
  sex: 'm' | 'f';
  cents: number;
  paidCents: number;
  payer: string;
  reference: string;
  postedIso: string;
  visitIso: string;
  dentist: string;
  stateName: string;
  /** Overrides "today"; for the item that appears elsewhere (day 60). */
  asOfIso?: string;
  unlisted?: boolean;
}

const usd = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FIXED_SEEDS: Seed[] = [
  { id: 'HL-CREDIT-HUGO', patientName: 'Hugo Brandt', sex: 'm', cents: 61200, paidCents: 145000, payer: 'Guardian', reference: 'check 77134406', postedIso: '2026-10-15', visitIso: '2026-09-25', dentist: 'Dr. Marcus Lee', stateName: 'California' },
  { id: 'HL-CREDIT-NORA', patientName: 'Nora Quinn', sex: 'f', cents: 18800, paidCents: 94000, payer: 'Sun Life', reference: 'check 60418822', postedIso: '2026-10-04', visitIso: '2026-09-12', dentist: 'Dr. Emily Carter', stateName: 'California' },
  // Texas: never in the list or the counts. Direct link only, so the "elsewhere" version can be seen.
  { id: 'HL-CREDIT-WADE', patientName: 'Wade Lin', sex: 'm', cents: 30500, paidCents: 88000, payer: 'Cigna', reference: 'EFT trace 1T740582213', postedIso: '2026-10-06', visitIso: '2026-09-18', dentist: 'Dr. Emily Carter', stateName: 'Texas', unlisted: true },
];

function build(seed: Seed, californiaListed: boolean): HitlItem {
  const first = seed.patientName.split(' ')[0] ?? seed.patientName;
  const ca = seed.stateName === 'California';
  const appearIso = addDays(seed.postedIso, ca ? CA_APPEAR_DAYS : OTHER_APPEAR_DAYS);
  const asOfIso = seed.asOfIso ?? (ca ? CALIFORNIA_TODAY_ISO : appearIso);
  const tellByIso = ca ? addDays(seed.postedIso, CA_NOTICE_DAYS) : undefined;
  const daysLeft = tellByIso ? daysBetween(asOfIso, tellByIso) : undefined;
  const daysOnAccount = daysBetween(seed.postedIso, asOfIso);
  const he = seed.sex === 'm';
  const amount = usd(seed.cents);
  const paid = usd(seed.paidCents);
  const visitDate = fmtMonthDay(seed.visitIso);
  const posted = fmtMonthDay(seed.postedIso);

  const credit: PatientCredit = {
    patientName: seed.patientName,
    patientFirst: first,
    possessive: he ? 'his' : 'her',
    objective: he ? 'him' : 'her',
    amount,
    paidAtVisit: paid,
    payer: seed.payer,
    reference: seed.reference,
    visitLabel: visitDate,
    dentist: seed.dentist,
    postedIso: seed.postedIso,
    asOfIso,
    daysOnAccount,
    stateName: seed.stateName,
    ...(tellByIso ? { tellByIso } : {}),
    ...(daysLeft !== undefined ? { daysLeft } : {}),
    remindOffered: daysLeft === undefined || daysLeft > REMIND_CUTOFF_DAYS,
    ...(ca ? {} : { ruleLine: stateNote(seed.stateName) }),
  };

  const tries: Try[] = [
    { time: posted, channel: '', result: `Posted ${seed.payer}’s ${amount}. ${PMS} shows ${first}’s account ${amount} in credit.`, ok: true, logEntryId: CREDIT_LOG_IDS.posted },
    { time: fmtMonthDay(appearIso), channel: '', result: `Checked ${PMS}: the credit is still there, not refunded or used on a visit. ${first} has no visit booked.`, ok: true, logEntryId: CREDIT_LOG_IDS.checked },
  ];

  const cameHere = fmtMonthDay(appearIso);
  return {
    id: seed.id,
    kind: 'credit',
    kindLabel: 'Patient credit',
    group: creditGroup(daysLeft),
    title: `${seed.patientName} has ${amount} of credit`,
    metaLine: `${seed.payer} · ${seed.reference} · posted ${posted} · visit of ${visitDate} · ${seed.dentist}`,
    listContext: `${seed.payer} paid after ${first} paid in full`,
    deadlineText: tellByIso ? `Tell ${first} by ${fmtMonthDay(tellByIso)}` : `Day ${String(OTHER_APPEAR_DAYS)} · ${fmtMonthDay(appearIso)}`,
    deadlineWarn: daysLeft !== undefined && daysLeft <= REMIND_CUTOFF_DAYS,
    why: `${first} paid the full ${paid} at ${credit.possessive} visit. ${seed.payer} then paid the practice ${amount} for the same visit, so ${amount} on ${credit.possessive} account is ${credit.possessive} money. It has been on ${credit.possessive} account for ${String(daysOnAccount)} days. Refunding it or keeping it is a person’s decision; ELVA doesn’t move money.`,
    tries,
    credit,
    savePrimary: 'Save and let ELVA check',
    nextMeta: '',
    // A person always decides this one: there is no "I can't do this" on this type.
    cantDo: { title: '', reasons: [], notice: '' },
    history: [`posted ${posted} · checked ${cameHere} · came here ${cameHere}`],
    doneFindings: [],
    ...(seed.unlisted || !californiaListed ? { unlisted: true } : {}),
  };
}

/**
 * The credit items. Fixed ids resolve in every scenario (direct links work);
 * only under `?scenario=california` do Hugo and Nora join the list and the counts.
 */
export function buildCreditItems(californiaListed: boolean): HitlItem[] {
  return FIXED_SEEDS.map((s) => build(s, californiaListed));
}
